import * as React from "react";
import {
  Card,
  Box,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Checkbox,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ZoomInIcon from "@mui/icons-material/ZoomIn";

export type PreviewKind = "image" | "video";

export type FileCardProps = {
  id: string;
  file: File;
  preview?: string;
  busy: boolean;
  onRemove: (id: string) => void;
  onPreview?: (src: string, name: string, kind: PreviewKind) => void;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  isMobile?: boolean;
};

function useInView<T extends Element>() {
  const ref = React.useRef<T | null>(null);
  const [inView, setInView] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [inView]);

  return { ref, inView };
}

const FileCard = ({
  id,
  file,
  preview,
  busy,
  onRemove,
  onPreview,
  selected = false,
  onToggleSelect,
  isMobile = false,
}: FileCardProps) => {
  const { ref, inView } = useInView<HTMLDivElement>();
  const isImage = file.type.startsWith("image/");
  const isVideo = file.type.startsWith("video/");
  const showOverlay = (isImage || isVideo) && Boolean(preview);
  const sizeInMb = (bytes: number) => (bytes / 1024 / 1024).toFixed(2);
  const thumbnailHeight = isMobile ? 126 : 156;

  return (
    <Card
      elevation={0}
      aria-selected={selected}
      sx={(theme) => ({
        width: "100%",
        overflow: "hidden",
        position: "relative",
        userSelect: "none",
        borderRadius: 1,
        color: "text.primary",
        background:
          "linear-gradient(180deg, rgba(255,255,255,.035), rgba(255,255,255,.012))",
        border: "1px solid",
        borderColor: selected
          ? theme.palette.primary.main
          : alpha(theme.palette.primary.main, 0.22),
        boxShadow: selected
          ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.4)}, 0 16px 34px rgba(0,0,0,.32)`
          : "0 12px 28px rgba(0,0,0,.22)",
        transition:
          "transform .18s ease, border-color .18s ease, box-shadow .18s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: alpha(theme.palette.primary.main, 0.55),
          boxShadow: "0 18px 38px rgba(0,0,0,.34)",
        },
      })}
    >
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={(theme) => ({
          position: "absolute",
          top: 10,
          left: 10,
          zIndex: 4,
          display: "grid",
          placeItems: "center",
          borderRadius: 0.75,
          backgroundColor: "rgba(10,10,10,.78)",
          border: `1px solid ${alpha(theme.palette.primary.main, 0.34)}`,
          backdropFilter: "blur(8px)",
          boxShadow: "0 6px 18px rgba(0,0,0,.28)",
        })}
      >
        <Checkbox
          size={isMobile ? "small" : "medium"}
          checked={selected}
          onChange={() => onToggleSelect?.(id)}
          sx={(theme) => ({
            p: 0.75,
            color: alpha(theme.palette.primary.main, 0.72),
            "&.Mui-checked": {
              color: theme.palette.primary.main,
            },
          })}
        />
      </Box>

      <Box
        ref={ref}
        sx={(theme) => ({
          height: thumbnailHeight,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          position: "relative",
          backgroundColor: "#0A0A0A",
          backgroundImage: `
            linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 34%),
            radial-gradient(circle at 100% 0%, ${alpha(theme.palette.primary.main, 0.09)}, transparent 44%),
            linear-gradient(180deg, #151515 0%, #090909 100%)
          `,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
        })}
      >
        {isImage && preview && (
          <img
            src={inView ? preview : undefined}
            alt={file.name}
            loading="lazy"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        )}

        {isVideo &&
          (inView && preview ? (
            <video
              src={preview}
              preload="metadata"
              playsInline
              muted
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <EmptyPreview fileName={file.name} />
          ))}

        {!isImage && !isVideo && <EmptyPreview fileName={file.name} />}

        {showOverlay && (
          <Box
            role="button"
            tabIndex={0}
            aria-label={`Podgląd: ${file.name}`}
            onClick={(event) => {
              event.stopPropagation();
              onPreview?.(preview!, file.name, isImage ? "image" : "video");
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onPreview?.(preview!, file.name, isImage ? "image" : "video");
              }
            }}
            sx={(theme) => ({
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              backgroundColor: {
                xs: "rgba(0,0,0,.28)",
                md: "rgba(0,0,0,.06)",
              },
              transition: "background-color .2s ease",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,.42)",
                "& .preview-button": {
                  transform: "scale(1.05)",
                  borderColor: theme.palette.primary.light,
                  backgroundColor: "rgba(16,16,16,.94)",
                },
              },
            })}
          >
            <IconButton
              size="large"
              className="preview-button"
              sx={(theme) => ({
                width: 54,
                height: 54,
                color: theme.palette.primary.light,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.58)}`,
                backgroundColor: "rgba(10,10,10,.84)",
                backdropFilter: "blur(8px)",
                boxShadow: "0 12px 24px rgba(0,0,0,.38)",
                transition:
                  "transform .18s ease, border-color .18s ease, background-color .18s ease",
                "&:hover": {
                  backgroundColor: "rgba(16,16,16,.96)",
                },
              })}
            >
              {isImage ? <ZoomInIcon /> : <PlayCircleOutlineIcon />}
            </IconButton>
          </Box>
        )}
      </Box>

      <Box sx={{ px: 2, pt: 1.5, pb: 1.25 }}>
        <Typography
          variant="body2"
          noWrap
          title={file.name}
          sx={{
            fontWeight: 600,
            letterSpacing: ".01em",
          }}
        >
          {file.name}
        </Typography>

        {!isMobile && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.35,
              color: "text.secondary",
              letterSpacing: ".04em",
            }}
          >
            {sizeInMb(file.size)} MB
          </Typography>
        )}
      </Box>

      <Box sx={{ px: 1.25, pb: 1.25 }}>
        <Tooltip title="Usuń">
          <span style={{ display: "block", width: "100%" }}>
            <Button
              fullWidth
              color="error"
              variant="outlined"
              startIcon={<DeleteOutlineIcon />}
              disabled={busy}
              onClick={(event) => {
                event.stopPropagation();
                onRemove(id);
              }}
              sx={(theme) => ({
                minHeight: 38,
                borderRadius: 0.75,
                fontSize: ".76rem",
                letterSpacing: ".08em",
                color: alpha(theme.palette.error.light, 0.92),
                borderColor: alpha(theme.palette.error.main, 0.38),
                backgroundColor: alpha(theme.palette.error.main, 0.025),
                "&:hover": {
                  borderColor: alpha(theme.palette.error.main, 0.72),
                  backgroundColor: alpha(theme.palette.error.main, 0.09),
                },
              })}
            >
              Usuń
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Card>
  );
};

function EmptyPreview({ fileName }: { fileName: string }) {
  return (
    <Stack
      alignItems="center"
      spacing={1}
      sx={{
        width: "100%",
        px: 2,
        color: "text.secondary",
      }}
    >
      <InsertDriveFileIcon sx={{ fontSize: 34, color: "primary.main" }} />
      <Typography variant="caption" noWrap sx={{ maxWidth: "100%" }}>
        {fileName}
      </Typography>
    </Stack>
  );
}

export default React.memo(FileCard);
